// Pure calculations shared between the live callouts in the modal and
// the server-side payload builder.

import { nonHostAttendeeCount } from "../../../lib/eventMoney";

// Compute event duration in hours from start/end date+time strings.
// Returns 0 if any piece is missing or the range is invalid (end <= start).
export const computeEventHours = (formData) => {
  if (!formData.startDate || !formData.startTime) return 0;
  if (!formData.endDate || !formData.endTime) return 0;
  const start = new Date(`${formData.startDate}T${formData.startTime}`);
  const end = new Date(`${formData.endDate}T${formData.endTime}`);
  const diffMs = end - start;
  if (isNaN(diffMs) || diffMs <= 0) return 0;
  return diffMs / (1000 * 60 * 60);
};

// "Approved" means the venue has signed off (or there's no venue gate at
// all because the host is hosting it themselves). Once an event is
// approved, location + start/end time are locked in — moving them would
// invalidate the venue's commitment and confuse anyone with a ticket.
export const isEventApproved = (event) => {
  if (!event) return false;
  const isPartnerVenue = !!event.partnerVenueId;
  if (!isPartnerVenue) return true; // self-hosted events have no external gate
  return event.venueApprovalStatus === "approved";
};

// "Confirmed" is a stricter state on top of approved: the event has also
// hit the partner-venue ticket-revenue minimum (if any). Buyers have
// committed at the current ticket price and capacity, so those fields
// are off-limits too. Self-hosted events don't have a minimum to hit, so
// they're confirmed the moment they exist.
export const isEventConfirmed = (event) => {
  if (!event) return false;
  if (!isEventApproved(event)) return false;

  const isPartnerVenue = !!event.partnerVenueId;
  const ticketPrice = Number(event.cost) || 0;
  const minRevenue = Number(event.venueMinimumRevenue) || 0;
  const minTickets =
    isPartnerVenue && minRevenue > 0 && ticketPrice > 0
      ? Math.ceil(minRevenue / ticketPrice)
      : 0;
  // Host doesn't count toward the venue minimum — they aren't paying
  // for their own venue fee, so keep this in lockstep with the
  // attendance bar in `EventsPage`.
  const attendees = nonHostAttendeeCount(event);

  return minTickets === 0 || attendees >= minTickets;
};

// Partner-venue math: how much revenue the host needs to collect to
// confirm the booking, and how many tickets that works out to at the
// currently set ticket price.
export const computeBreakEven = (formData) => {
  const hours = computeEventHours(formData);
  const pricePerHour = Number(formData._partnerMinPricePerHour) || 0;
  const minRevenue = Math.round(hours * pricePerHour * 100) / 100;

  const ticketPrice = Number(formData.cost) || 0;
  const capacity = Number(formData.maxMembers) || 0;
  const ticketsNeeded =
    ticketPrice > 0 && minRevenue > 0 ? Math.ceil(minRevenue / ticketPrice) : 0;

  return {
    hours,
    pricePerHour,
    minRevenue,
    ticketPrice,
    capacity,
    ticketsNeeded,
    // `true` only when price + capacity can actually cover the minimum.
    feasible:
      ticketPrice > 0 &&
      ticketsNeeded > 0 &&
      (capacity === 0 || ticketsNeeded <= capacity),
    capacityOverflow: capacity > 0 && ticketsNeeded > capacity,
  };
};
